/**
 *
 * @module paymentMethodInformation
 *
 */

import Check from 'check-types';
import Services from './../services/entry';

const BillingAddress = Services.Commerce.BillingAddress;

const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    number,
    nonEmptyString,
    nonEmptyObject,
    instanceStrict,
    boolean
} = Check.assert;

/**
 *
 * @class PaymentMethodInformation
 * @since 3.5.0
 * @desc Provides information about a new payment method to add for commerce transactions.
 *
 */
export default class PaymentMethodInformation {

    /**
     *
     * @param {Object} options
     * @param {Boolean} options.isDefault=true
     * @param {String} options.creditCardNumber
     * @param {String} [options.alternateName=undefined]
     * @param {SDK.Services.Commerce.BillingAddress} options.billingAddress
     * @param {Number} options.expiryMonth
     * @param {Number} options.expiryYear
     * @param {String} options.ownerFullName
     * @param {Boolean} options.isReusable
     * @throws {InvalidArgumentException}
     *
     */
    constructor(options) {

        const errorMessage = {
            ctor: 'PaymentMethodInformation(options)',
            get options() {
                return `${this.ctor} options should be a non-empty Object`;
            },
            get isDefault() {
                return `${this.ctor} options.isDefault should be a Boolean`;
            },
            get creditCardNumber() {
                return `${this.ctor} options.creditCardNumber should be a String with length`;
            },
            get alternateName() {
                return `${this.ctor} options.alternateName should be a String with length`;
            },
            get billingAddress() {
                return `${this.ctor} options.billingAddress should be an instance of SDK.Services.Commerce.BillingAddress`; // eslint-disable-line max-len
            },
            get expiryMonth() {
                return `${this.ctor} options.expiryMonth should be a Number`;
            },
            get expiryYear() {
                return `${this.ctor} options.expiryYear should be a Number`;
            },
            get ownerFullName() {
                return `${this.ctor} options.ownerFullName should be a String with length`;
            },
            get isReusable() {
                return `${this.ctor} options.isReusable should be a Boolean`;
            }
        };

        nonEmptyObject(options, errorMessage.options, InvalidArgumentException);

        const { isDefault = true, creditCardNumber, alternateName, billingAddress } = options;
        const { expiryMonth, expiryYear, ownerFullName, isReusable } = options;

        boolean(isDefault, errorMessage.isDefault, InvalidArgumentException);
        nonEmptyString(creditCardNumber, errorMessage.creditCardNumber, InvalidArgumentException);

        if (Check.assigned(alternateName)) {
            nonEmptyString(alternateName, errorMessage.alternateName, InvalidArgumentException);
        }

        instanceStrict(billingAddress, BillingAddress, errorMessage.billingAddress, InvalidArgumentException);
        number(expiryMonth, errorMessage.expiryMonth, InvalidArgumentException);
        number(expiryYear, errorMessage.expiryYear, InvalidArgumentException);
        nonEmptyString(ownerFullName, errorMessage.ownerFullName, InvalidArgumentException);
        boolean(isReusable, errorMessage.isReusable, InvalidArgumentException);

        /**
         *
         * @access public
         * @type {Boolean}
         * @desc Flag to mark this payment method as default (defaults to true)
         *
         */
        this.isDefault = isDefault;

        /**
         *
         * @access public
         * @type {String}
         * @desc The valid credit card number.
         *
         */
        this.creditCardNumber = creditCardNumber;

        /**
         *
         * @access public
         * @type {String}
         * @desc Alias (nickname) for the payment method.
         *
         */
        this.alternateName = alternateName;

        /**
         *
         * @access public
         * @type {SDK.Services.Commerce.BillingAddress}
         * @desc A postal address.
         *
         */
        this.billingAddress = billingAddress;

        /**
         *
         * @access public
         * @type {Number}
         * @desc Expiry month of a payment method.
         *
         */
        this.expiryMonth = expiryMonth;

        /**
         *
         * @access public
         * @type {Number}
         * @desc Expiry year of a payment method.
         *
         */
        this.expiryYear = expiryYear;

        /**
         *
         * @access public
         * @type {String}
         * @desc Card holder full name.
         *
         */
        this.ownerFullName = ownerFullName;

        /**
         *
         * @access public
         * @type {Boolean}
         * @desc Flag to mark this payment method as reusable for future transactions.
         * @note The value of this field will need to be translated to one of two possible string values for
         * the usage field of Bam.Sdk.Services.Commerce.CreatePaymentMethodPassthroughData:
         * A true value maps to "multi_use", and a false value maps to "single_use".
         * These strings and their format are defined by the service.
         *
         */
        this.isReusable = isReusable;

    }

}



// WEBPACK FOOTER //
// ./src/commerce/paymentMethodInformation.js